import { Injectable } from '@angular/core';
import { DataService } from './data.service';
import { UserFieldActivity } from '../entities/UserFieldActivity';
import { Subject } from 'rxjs';
import { InFilter } from '../baseServices/filter.interface';
import { GlobalConstants } from '../constant/constant';
import { StoreName } from '../entities/StoreName';
import { DataChangeEmitPayload, DataChangeEmitWho, DataChangeEmitWhat } from '../models/data-change-emit/data-change-emit-payload';
import { DbTransaction } from '../models/db/db-transaction';
import { Callback, CallbackNames } from '../models/db/db-transaction-callback';
import { EntityHelper, Check } from '../utils/entity-helper';
import { RecordNotFoundException, RecordSoftDeletedException } from '../utils/exceptions';

@Injectable({
  providedIn: 'root',
})
export class UserFieldActivityService extends DataService {
  private emitChangeSource = new Subject<DataChangeEmitPayload<UserFieldActivity>>();
  changeEmitted$ = this.emitChangeSource.asObservable();

  constructor() {
    super();
  }

  // #region Utility

  emitChange(userFieldActivity: UserFieldActivity, who: DataChangeEmitWho, what: DataChangeEmitWhat) {
    this.emitChangeSource.next({ data: userFieldActivity, who: who, what: what });
  }

  getEmitChangeCallback(userFieldActivity: UserFieldActivity, who: DataChangeEmitWho, what: DataChangeEmitWhat): Callback {
    return {
      name: CallbackNames.EmitChangeForUserFieldActivity + '_' + userFieldActivity.RecordId,
      fn: this.emitChange.bind(this, userFieldActivity, who, what),
    };
  }

  private async startTransactionIfNeeded(existingTransaction?: DbTransaction): Promise<DbTransaction> {
    return existingTransaction || (await this.repository.startTransaction([StoreName.Users2FieldActivity], 'readwrite'));
  }

  // #endregion
  // #region Low-level Get One

  async getUserFieldActivityByOnlineId(onlineId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<UserFieldActivity> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getByOnlineId<UserFieldActivity>(StoreName.Users2FieldActivity, onlineId, UserFieldActivity, transaction);

    if (result === null) {
      throw new RecordNotFoundException(onlineId);
    }

    const record = result as UserFieldActivity;

    if (this.checkIsUserFieldActivitySoftDeleted(record) && !includeDeleted) {
      throw new RecordSoftDeletedException(onlineId);
    }

    return record;
  }

  async getUserFieldActivityByRecordId(recordId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<UserFieldActivity> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getByLocalId<UserFieldActivity>(StoreName.Users2FieldActivity, recordId, UserFieldActivity, transaction);

    if (result === null) {
      throw new RecordNotFoundException(recordId);
    }

    const record = result as UserFieldActivity;

    if (this.checkIsUserFieldActivitySoftDeleted(record) && !includeDeleted) {
      throw new RecordSoftDeletedException(recordId);
    }

    return record;
  }

  // #endregion
  // #region Low-level Get Many

  async getAllUserFieldActivitiesByTeamId(teamId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<UserFieldActivity[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getAllByIndex<UserFieldActivity>(StoreName.Users2FieldActivity, GlobalConstants.KEY_NAMES.TeamId, teamId, UserFieldActivity, transaction);

    let records = result as UserFieldActivity[];

    if (!includeDeleted) {
      records = records.filter(userFieldActivity => !this.checkIsUserFieldActivitySoftDeleted(userFieldActivity));
    }

    return records;
  }

  async getUser2FieldActivitiesByUserId(userId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<UserFieldActivity[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getAllByIndex<UserFieldActivity>(StoreName.Users2FieldActivity, GlobalConstants.KEY_NAMES.UserId, userId, UserFieldActivity, transaction);

    let records = result as UserFieldActivity[];

    if (!includeDeleted) {
      records = records.filter(userFieldActivity => !this.checkIsUserFieldActivitySoftDeleted(userFieldActivity));
    }

    return records;
  }

  // #endregion
  // region Low-level Create

  private async createUserFieldActivity(who: DataChangeEmitWho, recordToCreate: UserFieldActivity, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<number> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // LC1. Validate action
    await this.validateUserFieldActivityCreation(transaction);

    // TODO: Impelment LC2 and LC3

    // LC4. Repository call
    const recordId = await this.repository.create(recordToCreate, StoreName.Users2FieldActivity, transaction);

    // LC5. Get created record
    const createdRecord = await this.getUserFieldActivityByRecordId(recordId, transaction, includeDeleted);

    // LC6. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(createdRecord, who, DataChangeEmitWhat.Create));

    // LC7. Return id
    return recordId;
  }

  // #endregion
  // region Low-level Update

  async updateUserFieldActivity(
    who: DataChangeEmitWho,
    recordToUpdate: UserFieldActivity,
    skipLocalCopyValidation: boolean,
    localCopy: UserFieldActivity | null,
    existingTransaction?: DbTransaction
  ): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // LU1. Get existing record
    const existingRecord = await this.getUserFieldActivityByRecordId(recordToUpdate.RecordId, transaction);

    // LU2. Validate action
    await this.validateUserFieldActivityUpdate(recordToUpdate, transaction);

    // LU3. Idempotent check
    if (EntityHelper.checkAllFieldsEqual(existingRecord, recordToUpdate)) {
      return;
    }

    // LU4. Validate local copy
    if (!skipLocalCopyValidation) {
      const checks: Check<UserFieldActivity>[] = [{ name: 'ImmutableFields', function: this.checkImmutableFieldsMatch }];
      EntityHelper.validateLocalCopy<UserFieldActivity>(localCopy, existingRecord, checks);
    }

    // LU5. Validate model
    await this.validateUserFieldActivity(recordToUpdate, transaction);

    // LU6. Repository call
    await this.repository.update(recordToUpdate, StoreName.Users2FieldActivity, transaction);

    // LU7. Get updated record
    const updatedRecord = await this.getUserFieldActivityByRecordId(recordToUpdate.RecordId, transaction);

    // LU8. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(updatedRecord, who, DataChangeEmitWhat.Update));

    // LU9. Return
    return;
  }

  // #endregion
  // region Low-level Delete

  // #endregion
  // region Low-level Validate Model

  async validateUserFieldActivity(userFieldActivity: UserFieldActivity, existingTransaction?: DbTransaction): Promise<void> {
    // TODO: Implement validation logic

    return;
  }

  // #endregion
  // region Low-level Validate Action

  async validateUserFieldActivityCreation(existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Implement validation logic

    return;
  }

  async validateUserFieldActivityUpdate(userFieldActivity: UserFieldActivity, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Implement validation logic

    return;
  }

  // #endregion
  // region Mid-level Get Many

  async getFieldActivityIdsByUserId(userId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<number[]> {
    const records = await this.getUser2FieldActivitiesByUserId(userId, existingTransaction, includeDeleted);
    return records.map(record => record.FieldActivityId);
  }

  // #endregion
  // region Mid-level Create

  // #endregion
  // region Mid-level Update

  // #endregion
  // region Mid-level Delete

  // #endregion
  // region Mid-level Validate

  // #endregion
  // region Mid-level Upsert

  // #endregion
  // region Mid-level Sync

  async upsertUserFieldActivityFromSync(who: DataChangeEmitWho, recordToUpsert: UserFieldActivity, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (who !== DataChangeEmitWho.Sync) {
      throw new Error('Must originate from Sync.');
    }

    try {
      const existingRecord = await this.getUserFieldActivityByOnlineId(recordToUpsert.Id, transaction, true);
      recordToUpsert.RecordId = existingRecord.RecordId;
      await this.updateUserFieldActivity(who, recordToUpsert, true, null, transaction);
    } catch (e) {
      if (e instanceof RecordNotFoundException) {
        await this.createUserFieldActivity(who, recordToUpsert, transaction, true);
      }
    }

    return;
  }

  // #endregion
  // region Check

  private checkIsUserFieldActivitySoftDeleted(record: UserFieldActivity): boolean {
    return record.IsDeleted;
  }

  private checkImmutableFieldsMatch(a: UserFieldActivity, b: UserFieldActivity): boolean {
    return a.FieldActivityId === b.FieldActivityId && a.UserId === b.UserId;
  }

  // #endregion
  // region Lookup

  // #endregion
  // region Transform

  // #endregion
  // region Other

  // #endregion
}
